import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import backendApi from "../api/backendApi";
import Field from "../components/ui/Field";
import { FiArrowLeft } from "react-icons/fi";

const AdminTransactionDetailsPage = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tx, setTx] = useState(null);

  useEffect(() => {
    let ignore = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await backendApi.get(`/payment/transactions/${id}`);

        if (!ignore) setTx(res?.data);
      } catch (e) {
        if (!ignore)
          setError(
            e?.response?.data?.message ||
              e.message ||
              "Failed to load transaction"
          );
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => {
      ignore = true;
    };
  }, [id]);

  function fmtAmount(v) {
    const n = Number(v ?? 0);
    return n.toLocaleString("en-MY", { style: "currency", currency: "MYR" });
  }

  function fmtDate(v) {
    if (!v) return "N/A";
    const d = new Date(v).toLocaleString("en-MY", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Kuala_Lumpur",
    });
    return d;
  }

  const formatCategoryName = (name = "") =>
    name.split(/\s+/).filter(Boolean).map(formatToken).join(" ");

  const formatToken = (t) =>
    t === "TRX_FEE"
      ? "Transaction Fee"
      : t
          .toLowerCase()
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");

  if (loading) return <div className="p-6">Loading…</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!tx) return <div className="p-6">Not found</div>;

  const payer = tx.payer || {};
  const cats = tx.paymentCategories || tx.categories || [];

  return (
    <div className="">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl shadow p-6">
          <div className="flex items-center gap-3 mb-3">
            <Link
              className="bg-white text-2xl border rounded-full p-1"
              to="/admin/transactions"
            >
              <FiArrowLeft />
            </Link>
            <h1 className="text-2xl font-semibold">Transaction Details</h1>
          </div>
          <h2 className="font-semibold mb-4">Overview</h2>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Transaction ID" value={tx.paymentReference} />
            <Field label="Status" value={tx.status} />
            <Field label="Amount" value={fmtAmount(tx.amount)} />
            <Field label="Method" value={tx.method} />
            <Field label="Created" value={fmtDate(tx.createdAt)} />
            <Field label="Paid At" value={fmtDate(tx.paymentDate)} />
          </div>

          <div className="mt-6">
            <h3 className="font-medium mb-2">Breakdown</h3>
            <div className="border rounded-xl overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-zinc-50 text-left">
                  <tr>
                    <th className="px-4 py-3 font-medium">Category</th>
                    <th className="px-4 py-3 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {cats?.length ? (
                    cats.map((pc) => (
                      <tr className="border-t" key={pc.id}>
                        <td className="px-4 py-3">
                          {formatCategoryName(pc.category?.name)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {fmtAmount(pc.amount)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className="px-4 py-4" colSpan={2}>
                        No breakdown.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow p-6">
          <h2 className="font-semibold mb-4">Payer Details</h2>
          <div className="space-y-2 text-sm">
            <Field
              label="Name"
              value={formatCategoryName(payer.firstName + "_" + payer.lastName)}
            />
            <Field label="Email" value={payer.email} />
            <Field label="AIMS No" value={payer.aimsNo} />
            <Field label="Muqami" value={formatCategoryName(payer.muqami)} />
          </div>
        </div>
      </div>

      {tx.status === "FAILED" && (
        <div className="p-4 border border-red-200 rounded-xl bg-red-50 text-red-700">
          <div className="font-medium mb-1">Failure Reason</div>
          <div className="text-sm whitespace-pre-wrap">
            {tx.errorMessage || tx.failureReason || tx.gatewayMessage || "-"}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTransactionDetailsPage;
